import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  Button as MuiButton,
  styled,
  Container,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  getAuth,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  PhoneAuthProvider,
  signInWithCredential,
} from "firebase/auth";
import Routes from "../../../utils/Routes";
import Alert from "../../../components/Alert";
import Logo from "../../../assets/images/Logo.png";
import TextBox from "../../../components/TextField";
import Button from "../../../components/Button";
import OtpInput from 'react-otp-input';

function ProfileOTPSubmit({ className }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { verificationId, phone } = location.state || {};
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const auth = getAuth();

  // const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otp, setOtp] = useState('');

  const [confirmationResponse, setConfirmationResponse] =
    useState(verificationId);
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(300);
  const otpRefs = useRef([]);

  useEffect(() => {
    otpRefs.current = otpRefs.current.slice(0, 6);
  }, []);

  const timeOutCallback = useCallback(() => {
    setTimer((currTimer) => (currTimer > 0 ? currTimer - 1 : 0));
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const timeout = setTimeout(timeOutCallback, 1000);
      return () => clearTimeout(timeout);
    }
  }, [timer, timeOutCallback]);

  const otpSubmitHandler = async () => {
    if (otp.join("").length !== 6) {
      setAlert({
        open: true,
        message: "Please enter a 6-digit OTP",
        severity: "warning",
      });
      return;
    }

    try {
      setIsLoading(true);
      if (!confirmationResponse) {
        setAlert({
          open: true,
          message: "There was an error sending the One Time Password",
          severity: "error",
        });
        return;
      }

      const credential = PhoneAuthProvider.credential(
        confirmationResponse,
        otp.join("")
      );

      const userCredential = await signInWithCredential(auth, credential);

      setAlert({
        open: true,
        message: "OTP verified successfully!",
        severity: "success",
      });
      setIsLoading(false);
      navigate("/" + Routes.CreateNewPasswordScreen);
    } catch (error) {
      setIsLoading(false);
      setAlert({
        open: true,
        message: error.message || "One Time Password verification failed!",
        severity: "error",
      });
    }
  };

  const resendOtpHandler = async () => {
    if (timer === 0) {
      setTimer(300);
      setIsLoading(true);

      try {
        window.recaptchaVerifier = new RecaptchaVerifier(
          auth,
          "recaptcha-container",
          {
            size: "invisible",
          }
        );

        const newConfirmationResponse = await signInWithPhoneNumber(
          auth,
          `+1${phone}`,
          window.recaptchaVerifier
        );
        setConfirmationResponse(newConfirmationResponse);
        setAlert({
          open: true,
          message: "One Time Password sent successfully!",
          severity: "success",
        });
      } catch (error) {
        setAlert({
          open: true,
          message:
            error.message ||
            "Failed to send One Time Password. Please try again!",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Allow only numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move focus to next box if filled
    if (value && index < 5) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;

  return (
    <div className={className}>
      <Container maxWidth="sm" sx={{ textAlign: "center", mt: 5 }}>
        {/* Snackbar Alert */}
        <Alert
          open={alert.open}
          message={alert.message}
          severity={alert.severity}
          onClose={() => setAlert({ ...alert, open: false })}
        />

        {/* Logo */}
        <Box display="flex" flexDirection="column" alignItems="center">
          <img src={Logo} alt="Logo" className="logo" />
          <div className="heading">Enter One Time Password</div>
        </Box>

        {/* OTP Input */}

{/*         <Box className="otpContainer">
          {otp.map((value, index) => (
            <TextBox
              key={index}
              className="otpInput"
              value={value}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              inputRef={(el) => (otpRefs.current[index] = el)}
              maxLength={1}
            />
          ))}
        </Box> */}
    <OtpInput
      value={otp}
      onChange={setOtp}
      numInputs={6}
      renderSeparator={<span>-</span>}
      renderInput={(props) => <input {...props} />}
    />

        {/* Timer & Resend OTP */}
        <Box className="resendContainer">
          <Typography variant="body2">Didn't receive OTP?</Typography>
          {timer === 0 ? (
            <MuiButton
              variant="text"
              className="resendBtn"
              onClick={resendOtpHandler}
            >
              Resend
            </MuiButton>
          ) : (
            <Typography variant="body2" sx={{ color: "green" }}>
              {Math.floor(timer / 60)}:
              {timer % 60 < 10 ? `0${timer % 60}` : timer % 60}s
            </Typography>
          )}
        </Box>

        {/* Submit Button */}
        <Button label="Submit" onClick={otpSubmitHandler} />
      </Container>
    </div>
  );
}

export default styled(ProfileOTPSubmit)(() => ({
  ".logo": {
    width: "100px",
    height: "100px",
  },
  ".heading": {
    fontWeight: 600,
    fontSize: 22,
    letterSpacing: 0,
    textAlign: "center",
    color: "#234000",
    marginTop: "3%",
  },
  ".otpContainer": {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "20px",
  },
  ".otpInput": {
    width: "50px",
    height: "50px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "bold",
  },
  ".resendContainer": {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
  },
  ".resendBtn": {
    textTransform: "none",
    padding: 0,
    color: "#000000",
    fontWeight: 500,
  },
}));
